import { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import { RolesProvider } from './hooks/roles/RolesContext';
// Sign-in pages stay eager so the first screen never waits on a chunk.
import { Login } from './components/auth/Login';
import { AuthCallback } from './components/auth/AuthCallback';
import { AuthSso } from './components/auth/AuthSso';
import { ScrAppHandoff } from './components/auth/ScrAppHandoff';
import { SupabaseProvider } from './services/supabase';
import { ToastProvider } from './components/ui/toast';
import { ConfirmProvider } from './components/ui/confirm-dialog';
import { VersionBadge } from './components/VersionBadge';
import { CommandPalette } from './components/common/CommandPalette';

// Each module's pages load lazily from these route tables (#12).
import { RequireAccess } from './routes/RequireAccess';
import { PageFallback } from './routes/PageFallback';
import { documentsRoutes } from './routes/documentsRoutes';
import { caseNotesStaffRoutes, caseNotesFullAccessRoutes } from './routes/caseNotesRoutes';
import { keystoneMemberRoutes, keystoneLeaderRoutes } from './routes/keystoneRoutes';
import { staffRoutes } from './routes/staffRoutes';
import { parentRoutes, teacherRoutes } from './routes/portalRoutes';
import { legacyRedirectRoutes } from './routes/legacyRedirects';

// Every staff member lands on Home (#377); it shows each role only what it
// can use, including a Keystone shortcut for leaders.
const StaffDefaultRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex w-full min-h-screen justify-center items-center">
        Loading...
      </div>
    );
  }

  if (!user || user.role !== 'staff') {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to="/staff/home" replace />;
};

export default function App() {
  return (
    <SupabaseProvider>
      <AuthProvider>
        <RolesProvider>
        <ToastProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <div className="min-h-screen bg-gray-50">
              <VersionBadge />
              {/* ⌘K / Ctrl+K student search, staff only (#363) */}
              <CommandPalette />
              <Suspense fallback={<PageFallback />}>
              <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              {/* Platform launchpad hand-off — additive, does not affect /login */}
              <Route path="/auth/sso" element={<AuthSso />} />
              {/* SCR App one-tap sign-in (in-app browser only); falls back to /login?sso=entra */}
              <Route path="/scrapp-handoff" element={<ScrAppHandoff />} />

              <Route path="/staff" element={<StaffDefaultRedirect />} />

              <Route element={<RequireAccess rule="staff" />}>
                {staffRoutes}
                {caseNotesStaffRoutes}
              </Route>

              <Route element={<RequireAccess rule="fullAccess" />}>
                {documentsRoutes}
                {caseNotesFullAccessRoutes}
              </Route>

              <Route element={<RequireAccess rule="keystoneMember" />}>
                {keystoneMemberRoutes}
              </Route>
              <Route element={<RequireAccess rule="keystoneLeader" />}>
                {keystoneLeaderRoutes}
              </Route>

              <Route element={<RequireAccess rule="teacher" />}>
                {teacherRoutes}
              </Route>

              {parentRoutes}

              {/* Old URLs; remove by 2027-02-01 (see routes/legacyRedirects.tsx) */}
              {legacyRedirectRoutes}

              {/* Default redirect */}
              <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
              </Suspense>
            </div>
          </BrowserRouter>
        </ConfirmProvider>
        </ToastProvider>
        </RolesProvider>
      </AuthProvider>
    </SupabaseProvider>
  );
}
